import { useMutation, useQueryClient } from '@tanstack/react-query';
import { timesheetService } from '@/services/timesheets/timesheetService';
import { timesheetQueryKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { notifyTimesheetStatus } from '@/services/notifications/triggerNotification';
import type { 
  ApproveTimeEntryInput, 
  EditTimeEntryInput,
  EditOwnTimeEntryInput,
  AddCommentInput,
  PayrollGenerationInput, 
  PayrollExportInput,
  CreateManualEntryInput,
  CreateBulkEntriesInput
} from '@/services/timesheets/types';
import { dashboardKeys } from '@/hooks/dashboard/queryKeys';

/** Error mapper for timesheet-related operations */
function mapTimesheetError(message?: string) {
  const m = (message ?? "").toLowerCase();
  if (m.includes("status_conflict")) return "This entry was already updated elsewhere. Refreshing…";
  if (m.includes("unauthorized")) return "You need to be signed in to perform this action.";
  if (m.includes("forbidden")) return "You don't have permission to perform this action.";
  if (m.includes("insufficient_permissions")) return "You don't have permission to add comments.";
  if (m.includes("not_found")) return "Time entry not found.";
  if (m.includes("cross_tenant_forbidden")) return "You cannot access time entries from another company.";
  if (m.includes("invalid_input")) return "Please provide valid input.";
  if (m.includes("comment text cannot be empty")) return "Comment cannot be empty.";
  if (m.includes("user profile not found")) return "Your user profile is not properly configured.";
  return "Something went wrong. Please try again.";
}

export const useTimesheetMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Approve or reject time entry
  const approveTimeEntry = useMutation({
    mutationFn: (input: ApproveTimeEntryInput) => timesheetService.approveTimeEntry(input),
    onSuccess: (result, variables) => {
      toast({
        title: 'Success',
        description: `Time entry ${variables.status} successfully`,
      });
      
      // Trigger notification (fire-and-forget)
      // Skip when profileId is null (orphaned entry from deleted employee — no recipient)
      if (result && result.profileId) {
        notifyTimesheetStatus({
          companyId: result.companyId,
          entryId: variables.entryId,
          status: variables.status,
          employeeProfileId: result.profileId,
        });
      }
      
      // Invalidate all company time entries with comments (current query pattern)
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company', 'withComments'],
      });
      
      // Also invalidate the base pattern for backward compatibility
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company'],
      });
    },
    onError: (error: Error) => {
      // Self-heal: if the row state has drifted (approved/rejected from
      // another session or device), pull fresh data so the UI matches reality.
      if ((error.message ?? "").toLowerCase().includes("status_conflict")) {
        queryClient.invalidateQueries({ queryKey: ['timesheets', 'company'] });
      }
      toast({
        title: 'Error',
        description: mapTimesheetError(error.message),
        variant: 'destructive',
      });
    },
  });

  // Revert time entry status (admin only)
  const revertTimeEntry = useMutation({
    mutationFn: (entryId: string) => timesheetService.revertTimeEntry(entryId),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Timesheet reverted to pending successfully',
      });
      
      // Invalidate all company time entries with comments (current query pattern)
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company', 'withComments'],
      });
      
      // Also invalidate the base pattern for backward compatibility
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company'],
      });
    },
    onError: (error: Error) => {
      if ((error.message ?? "").toLowerCase().includes("status_conflict")) {
        queryClient.invalidateQueries({ queryKey: ['timesheets', 'company'] });
      }
      toast({
        title: 'Error',
        description: mapTimesheetError(error.message),
        variant: 'destructive',
      });
    },
  });

  // Edit time entry clock out time
  const editTimeEntry = useMutation({
    mutationFn: (input: EditTimeEntryInput) => timesheetService.editTimeEntry(input),
    onSuccess: (_, variables) => {
      toast({
        title: 'Success',
        description: 'Time entry updated successfully',
      });
      
      // If a comment was added, invalidate comment-related caches
      if (variables.addComment) {
        // Invalidate the comments for this specific entry
        queryClient.invalidateQueries({
          queryKey: timesheetQueryKeys.timeEntryComments(variables.entryId),
        });
        
        // Invalidate company time entries with comments to update comment indicators
        queryClient.invalidateQueries({
          predicate: (query) => 
            query.queryKey[0] === 'timesheets' && 
            query.queryKey[1] === 'company' && 
            query.queryKey[2] === 'withComments'
        });
      }
      
      // Invalidate all company time entries with comments (current query pattern)
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company', 'withComments'],
      });
      
      // Also invalidate the base pattern for backward compatibility
      queryClient.invalidateQueries({
        queryKey: ['timesheets', 'company'],
      });

      // Invalidate dashboard metrics (weekly hours may have changed)
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.all,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to update time entry: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Add a comment to a time entry
  const addTimeEntryComment = useMutation({
    mutationFn: (input: AddCommentInput) => timesheetService.addTimeEntryComment(input),
    onSuccess: (_, variables) => {
      toast({
        title: 'Success',
        description: 'Comment added successfully',
      });
      
      // Invalidate the comments for this specific entry
      queryClient.invalidateQueries({
        queryKey: timesheetQueryKeys.timeEntryComments(variables.entryId),
      });
      
      // Invalidate company time entries with comments to update comment indicators
      queryClient.invalidateQueries({
        predicate: (query) => 
          query.queryKey[0] === 'timesheets' && 
          query.queryKey[1] === 'company' && 
          query.queryKey[2] === 'withComments'
      });
      
      // Also invalidate the base company time entries for backward compatibility
      queryClient.invalidateQueries({
        queryKey: timesheetQueryKeys.companyTimeEntries(),
      });
    },
    onError: (error: Error) => {
      console.error('Error adding comment:', error);
      toast({
        title: 'Error',
        description: mapTimesheetError(error.message),
        variant: 'destructive',
      });
    },
  });

  // Generate payroll (admin only)
  const generatePayroll = useMutation({
    mutationFn: (input: PayrollGenerationInput) => timesheetService.generatePayroll(input),
    onSuccess: (data) => {
      toast({
        title: 'Success',
        description: `Generated ${data.payslips_generated} payslips successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate payroll',
        variant: 'destructive',
      });
    },
  });

  // Export payroll data (admin only)
  const exportPayrollData = useMutation({
    mutationFn: async (input: PayrollExportInput) => {
      const blob = await timesheetService.exportPayrollData(input);
      
      // Download the CSV file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `payroll_${input.startDate}_to_${input.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return blob;
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Payroll data exported successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to export payroll data',
        variant: 'destructive',
      });
    },
  });

  // Create manual time entry
  const createManualEntry = useMutation({
    mutationFn: (input: CreateManualEntryInput) => timesheetService.createManualTimeEntry(input),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Manual time entry created successfully',
      });
      
      // Invalidate all timesheet queries to refresh data
      queryClient.invalidateQueries({
        queryKey: timesheetQueryKeys.all,
      });

      // Invalidate dashboard metrics (weekly hours may have changed)
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.all,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to create entry: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Edit own time entry (staff self-service with permission enforcement)
  const editOwnTimeEntry = useMutation({
    mutationFn: (input: EditOwnTimeEntryInput) => timesheetService.editOwnTimeEntry(input),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Time entry updated successfully',
      });
      
      // Invalidate personal timesheet queries
      queryClient.invalidateQueries({
        queryKey: timesheetQueryKeys.all,
      });

      // Invalidate dashboard metrics (weekly hours may have changed)
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.all,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to update time entry: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Create bulk time entries (manager backfilling employee timesheets)
  const createBulkEntries = useMutation({
    mutationFn: (input: CreateBulkEntriesInput) => timesheetService.createBulkTimeEntries(input),
    onSuccess: (result) => {
      const skippedMsg = result.skipped_dates.length > 0 
        ? ` (${result.skipped_dates.length} skipped due to conflicts)` 
        : '';
      toast({
        title: 'Success',
        description: `Created ${result.created_count} time entries${skippedMsg}`,
      });
      
      // Invalidate all timesheet queries to refresh data
      queryClient.invalidateQueries({
        queryKey: timesheetQueryKeys.all,
      });

      // Invalidate dashboard metrics (weekly hours may have changed)
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.all,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: mapTimesheetError(error.message),
        variant: 'destructive',
      });
    },
  });

  return {
    approveTimeEntry,
    revertTimeEntry,
    editTimeEntry,
    editOwnTimeEntry,
    addTimeEntryComment,
    generatePayroll,
    exportPayrollData,
    createManualEntry,
    createBulkEntries,
  };
};
